import type { ESN, PageableContent, UUID } from '@cobliteam/common-types';

export const CHECKLIST_VERSION = 2;

export enum ChecklistFieldType {
  CHECK = 'CHECK',
  TEXT = 'TEXT',
  MULTI_SELECT = 'MULTI_SELECT',
  SINGLE_SELECT = 'SINGLE_SELECT',
}

export interface ChecklistCheckboxContent {
  checked: boolean | null;
}

export interface ChecklistTextContent {
  value: string | null;
}

export interface ChecklistSelectOptionContent {
  label: string;
  checked: boolean | null;
}

export interface ChecklistSelectContent {
  options: ChecklistSelectOptionContent[];
}

export type ChecklistFieldContent = ChecklistCheckboxContent | ChecklistTextContent | ChecklistSelectContent;

export interface TemplateChecklistField {
  type: ChecklistFieldType;
  required: boolean;
  title: string;
  content: ChecklistFieldContent;
  shouldRequestPhotos: boolean;
  photosUrls?: string[];
}

export interface ChecklistField extends TemplateChecklistField {
  id: UUID;
}

export interface ChecklistVehicle {
  deviceId: ESN;
  licensePlate?: string;
  brand?: string;
  model?: string;
  groupId?: UUID;
}

export interface ChecklistContext {
  appVersion: string;
  address?: string;
  vehicle?: ChecklistVehicle;
  /**
   * Path of the vehicle's group, resolved server-side by ryuk when the response was created.
   * Prefer it over `vehicle.groupId`: that one is a snapshot the driver app copies from the
   * driver↔device association payload, and it stays undefined whenever the vehicle joined its
   * group after the association was opened (BUG-2708).
   */
  groupPath?: string;
}

export interface ChecklistConfiguredVehicle {
  id: UUID;
  groupPath: string;
  groupId: UUID;
}

export interface ChecklistConfiguredGroup {
  id: UUID;
  path: string;
}

export interface UpdatedChecklist {
  version: number;
  name: string;
  fields: TemplateChecklistField[];
  vehicleIds: UUID[] | null;
  vehicleGroupIds: UUID[] | null;
  template_active?: boolean;
}

export interface NewChecklist extends UpdatedChecklist {
  template: boolean;
}

export type TemplateChecklist = NewChecklist | UpdatedChecklist;

export interface Checklist extends NewChecklist {
  id: UUID;
  createdAt: number;
  createdBy: string;
  fields: ChecklistField[];
  templateId: UUID | null;
  templateCreatedAt: number;
  templateCreatedBy: string;
  context: ChecklistContext | null;
  vehicles: ChecklistConfiguredVehicle[];
  groups: ChecklistConfiguredGroup[];
  completedCount?: number;
  templateActive?: boolean;
  /** True when a field requiring photos hasn't received them yet — the response is listed either way. */
  photosPending?: boolean;
  pendingPhotosFieldCount?: number;
}

export interface ChecklistsNames {
  names: string[];
}

export interface ChecklistsPage {
  page: PageableContent<Checklist>;
}
